import * as React from "react"

import { cn } from "cn"

type OverlineTag = keyof React.JSX.IntrinsicElements

export type OverlineProps<TTag extends OverlineTag = "div"> =
  React.ComponentPropsWithRef<TTag> & {
    /** HTML element to render. Defaults to `div`. */
    tag?: TTag
  }

/**
 * Small uppercase muted caption used for secondary labels, field captions,
 * and meta text. Distinct from the form {@link Label} primitive in `ui/`.
 *
 * @example
 * ```tsx
 * <Overline>Currency</Overline>
 * <Overline tag="span" className="mr-1">Song Price:</Overline>
 * ```
 */
function Overline<TTag extends OverlineTag = "div">({
  tag,
  className,
  ...props
}: OverlineProps<TTag>) {
  const Tag = (tag ?? "div") as React.ElementType

  return (
    <Tag
      data-slot="overline"
      className={cn("text-xs uppercase text-muted-foreground", className)}
      {...props}
    />
  )
}

export { Overline }
